import React, { useEffect, useRef } from "react";
import { useZoneFloors } from "../../contexts/ZoneFloorsContext";
import { zoneColourFor } from "../../utils/heartRateZones";

/**
 * Rolling power chart rendered to a <canvas>. Subscribes to the FTMS hook
 * for high-rate (~10Hz) samples without going through React state.
 *
 * Performance:
 *  - Samples land in a ref-backed ring buffer; no setState on the hot path.
 *  - rAF redraws only while the canvas is on-screen (gated by IntersectionObserver).
 *  - Resizes via ResizeObserver so DPR-aware backing-store size matches CSS box.
 */
const PowerChart = ({
  subscribe,
  hr,
  hrBpm,
  windowSeconds = 60,
  className,
  lineColor = "rgb(250 204 21)", // yellow-400
  fillColor = "rgba(250, 204, 21, 0.18)",
  hrColor = "#ef4444", // red-500 — matches the post-session TelemetryChart
  axisColor = "rgba(255, 255, 255, 0.18)",
  labelColor = "rgba(255, 255, 255, 0.55)",
}) => {
  // Heart rate is drawn in zone colours (#194 follow-up), from the Profile.
  const floors = useZoneFloors();
  const floorsRef = useRef(floors);
  floorsRef.current = floors;
  const canvasRef = useRef(null);
  const samplesRef = useRef([]); // { t: ms, p: watts }
  const hrSamplesRef = useRef([]); // { t: ms, hr: bpm }
  const dimsRef = useRef({ w: 0, h: 0, dpr: 1 });
  const rafRef = useRef(0);
  const visibleRef = useRef(true);
  const dirtyRef = useRef(true);

  // HR source is read from a ref inside the heartbeat so a changing `hr`
  // wrapper / `hrBpm` poll doesn't tear down the draw loop + subscriptions.
  const hrSourceRef = useRef({ hr, hrBpm });
  hrSourceRef.current = { hr, hrBpm };

  // Draw loop — pulls from samplesRef. Never reads React state.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      rafRef.current = 0;
      if (!visibleRef.current) return;

      // Trim samples outside the window before drawing.
      const now = Date.now();
      const cutoff = now - windowSeconds * 1000;
      const samples = samplesRef.current;
      while (samples.length && samples[0].t < cutoff) samples.shift();
      const hrSamples = hrSamplesRef.current;
      while (hrSamples.length && hrSamples[0].t < cutoff) hrSamples.shift();

      const { w, h, dpr } = dimsRef.current;
      if (!w || !h) return;

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      // Axes
      ctx.strokeStyle = axisColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, h - 0.5);
      ctx.lineTo(w, h - 0.5);
      ctx.stroke();

      if (samples.length < 2 && hrSamples.length < 2) {
        ctx.fillStyle = labelColor;
        ctx.font = "12px ui-sans-serif, system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Waiting for data…", w / 2, h / 2);
        ctx.restore();
        return;
      }

      const xFor = (t) => ((t - cutoff) / (windowSeconds * 1000)) * w;

      // ── Power (primary): filled area + line on a watts scale. Determine y
      // scale from this window's max (rounded up to next 50W, floor 200W so a
      // light spin still looks like a chart).
      if (samples.length >= 2) {
        let maxP = 0;
        for (const s of samples) if (s.p > maxP) maxP = s.p;
        const yMax = Math.max(200, Math.ceil(maxP / 50) * 50 + 50);
        const yFor = (p) => h - (Math.max(0, p) / yMax) * (h - 4) - 2;

        ctx.beginPath();
        ctx.moveTo(xFor(samples[0].t), h);
        for (const s of samples) ctx.lineTo(xFor(s.t), yFor(s.p));
        ctx.lineTo(xFor(samples[samples.length - 1].t), h);
        ctx.closePath();
        ctx.fillStyle = fillColor;
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(xFor(samples[0].t), yFor(samples[0].p));
        for (let i = 1; i < samples.length; i++) {
          ctx.lineTo(xFor(samples[i].t), yFor(samples[i].p));
        }
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        // y-axis label (max watts)
        ctx.fillStyle = labelColor;
        ctx.font = "11px ui-sans-serif, system-ui, sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(`${yMax} W`, w - 4, 12);
      }

      // ── Heart rate (overlay): its own auto min/max scale so the trace isn't
      // crushed against the axis by the watts range. Mirrors TelemetryChart.
      if (hrSamples.length >= 2) {
        let lo = Infinity;
        let hi = -Infinity;
        for (const s of hrSamples) {
          if (s.hr < lo) lo = s.hr;
          if (s.hr > hi) hi = s.hr;
        }
        const pad = Math.max(5, (hi - lo) * 0.15);
        lo = Math.max(0, lo - pad);
        hi += pad;
        const span = hi - lo || 1;
        const yForHr = (v) => h - ((v - lo) / span) * (h - 4) - 2;

        const zoneFloorsNow = floorsRef.current;
        ctx.lineWidth = 2;
        if (zoneFloorsNow) {
          for (let i = 1; i < hrSamples.length; i++) {
            ctx.beginPath();
            ctx.moveTo(xFor(hrSamples[i - 1].t), yForHr(hrSamples[i - 1].hr));
            ctx.lineTo(xFor(hrSamples[i].t), yForHr(hrSamples[i].hr));
            ctx.strokeStyle = zoneColourFor(hrSamples[i].hr, zoneFloorsNow);
            ctx.stroke();
          }
        } else {
          ctx.beginPath();
          ctx.moveTo(xFor(hrSamples[0].t), yForHr(hrSamples[0].hr));
          for (let i = 1; i < hrSamples.length; i++) {
            ctx.lineTo(xFor(hrSamples[i].t), yForHr(hrSamples[i].hr));
          }
          ctx.strokeStyle = hrColor;
          ctx.stroke();
        }

        ctx.fillStyle = zoneFloorsNow
          ? zoneColourFor(hrSamples[hrSamples.length - 1].hr, zoneFloorsNow)
          : hrColor;
        ctx.font = "11px ui-sans-serif, system-ui, sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(
          `${Math.round(hrSamples[hrSamples.length - 1].hr)} bpm`,
          w - 4,
          26,
        );
      }

      // x-axis label (window)
      ctx.fillStyle = labelColor;
      ctx.font = "11px ui-sans-serif, system-ui, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(
        windowSeconds >= 120 ? `${Math.round(windowSeconds / 60)} min window` : `${windowSeconds}s window`,
        4,
        12,
      );

      ctx.restore();
    };

    const schedule = () => {
      if (rafRef.current) return;
      if (!visibleRef.current) return;
      rafRef.current = requestAnimationFrame(draw);
    };

    // Resize handling — keep backing store at device pixel ratio.
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      dimsRef.current = { w, h, dpr };
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      dirtyRef.current = true;
      schedule();
    };

    const resizeObs = new ResizeObserver(resize);
    resizeObs.observe(canvas);
    resize();

    // Visibility gating — pause draw loop when canvas leaves the viewport.
    const intObs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibleRef.current = entry.isIntersecting;
          if (visibleRef.current) schedule();
        }
      },
      { threshold: 0.1 },
    );
    intObs.observe(canvas);

    // Sample subscription — push to ring buffer, then schedule a redraw.
    const unsubscribe = subscribe?.((snapshot) => {
      if (snapshot.power_watts == null) return;
      samplesRef.current.push({ t: Date.now(), p: snapshot.power_watts });
      // Hard cap (defensive — also trimmed by time inside draw()).
      if (samplesRef.current.length > windowSeconds * 30) {
        samplesRef.current.shift();
      }
      schedule();
    });

    // Heartbeat: sample HR (~2 Hz from the strap/ESP32 fallback) and redraw so
    // the rolling window keeps scrolling even when power samples pause (coast).
    const heartbeat = setInterval(() => {
      const { hr: hrSrc, hrBpm: hrBpmSrc } = hrSourceRef.current;
      const bpm = hrSrc?.getFreshSnapshot?.(3000)?.bpm ?? hrBpmSrc;
      if (bpm != null) {
        hrSamplesRef.current.push({ t: Date.now(), hr: bpm });
        if (hrSamplesRef.current.length > windowSeconds * 5) {
          hrSamplesRef.current.shift();
        }
      }
      schedule();
    }, 500);

    return () => {
      resizeObs.disconnect();
      intObs.disconnect();
      unsubscribe?.();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearInterval(heartbeat);
    };
  }, [
    subscribe,
    windowSeconds,
    lineColor,
    fillColor,
    hrColor,
    axisColor,
    labelColor,
  ]);

  return <canvas ref={canvasRef} className={className} />;
};

export default PowerChart;
